import { create } from 'zustand'

import {
  conservativeServerCapabilities,
  normalizeServerEnabledCapabilities,
  type ServerEnabledCapabilities,
} from '@/pages/home/platform-capabilities'
import type { ApiEnvelope } from '@/shared/api/repositories/http-repositories'
import { request } from '@/utils/http/client'
import type { RequestConfig } from '@/utils/http/types'


interface BackendRuntimeConfig {
  schema_version: number
  version: number
  api_base_url: string
  web_base_url: string
  allow_insecure_http: boolean
  allow_insecure_http_until: string | null
  enabled_capabilities?: unknown
  updated_at: string
}

interface RuntimeConfigState {
  apiBaseUrl: string
  webBaseUrl: string
  allowInsecureHttp: boolean
  enabledCapabilities: ServerEnabledCapabilities
  version: number | null
  hydrated: boolean
  error: string | null
  bootstrap: () => Promise<void>
}

const browserOrigin = () =>
  typeof window === 'undefined' ? '' : window.location.origin.replace(/\/$/, '')

type RuntimeConfigRequester = (
  config: RequestConfig,
) => Promise<ApiEnvelope<BackendRuntimeConfig>>

export const createRuntimeConfigStore = (requestConfig: RuntimeConfigRequester) => {
  let bootstrapPromise: Promise<void> | null = null
  return create<RuntimeConfigState>((set, get) => ({
    apiBaseUrl: '/api/v1',
    webBaseUrl: browserOrigin(),
    allowInsecureHttp: false,
    enabledCapabilities: { ...conservativeServerCapabilities },
    version: null,
    hydrated: false,
    error: null,
    bootstrap: () => {
      if (get().hydrated) return Promise.resolve()
      bootstrapPromise ??= requestConfig({
        method: 'GET',
        url: '/public/runtime-config',
        skipAuth: true,
        skipRefresh: true,
      })
        .then(({ data }) => {
          set({
            apiBaseUrl: data.api_base_url,
            webBaseUrl: data.web_base_url.replace(/\/$/, ''),
            allowInsecureHttp: data.allow_insecure_http,
            enabledCapabilities: normalizeServerEnabledCapabilities(
              data.enabled_capabilities,
            ),
            version: data.version,
            hydrated: true,
            error: null,
          })
        })
        .catch((reason: unknown) => {
          set({
            webBaseUrl: browserOrigin(),
            enabledCapabilities: { ...conservativeServerCapabilities },
            hydrated: true,
            error: reason instanceof Error ? reason.message : '运行配置读取失败',
          })
        })
        .finally(() => {
          bootstrapPromise = null
        })
      return bootstrapPromise
    },
  }))
}

export const useRuntimeConfigStore = createRuntimeConfigStore((config) =>
  request<ApiEnvelope<BackendRuntimeConfig>>(config),
)

